import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Režim Performance — `/admin/api/v1/performance-mode` (admin API, ROLE_ADMIN,
 * feature `admin.performance-mode`).
 *
 * Dočasně sníží podrobnost komunikačních logů, aby zátěžový test nezaplnil databázi.
 * Vždy má konec: server dobu ořízne na `maxDurationMinutes` a režim vyprší sám.
 *
 * `expiresAtEpochMs` je epoch v milisekundách záměrně — odpočet si prohlížeč dopočítá
 * sám a nemusí se serveru ptát každou vteřinu.
 */
export type CommLogLevel = "FULL" | "NO_BODIES" | "ERRORS_ONLY" | "OFF";

/** Pořadí od nejpodrobnějšího po nejúspornější — v tomhle pořadí se nabízí i v UI. */
export const COMM_LOG_LEVELS: CommLogLevel[] = ["FULL", "NO_BODIES", "ERRORS_ONLY", "OFF"];

/** Nabízené doby běhu v minutách. Strop vynucuje server. */
export const PERF_MODE_DURATIONS = [15, 30, 60, 120, 240, 480];

export interface PerformanceModeState {
  active: boolean;
  level: CommLogLevel;
  expiresAtEpochMs: number;
  /** Zbývající čas podle serveru v okamžiku odpovědi; dál se tiká lokálně. */
  remainingMs: number;
  startedAtEpochMs: number;
  startedBy: string | null;
  suppressNotifications: boolean;
  suppressedNotifications: number;
  maxDurationMinutes: number;
}

export interface StartPerformanceModeRequest {
  level: CommLogLevel;
  durationMinutes: number;
  suppressNotifications: boolean;
}

const BASE = "/admin/api/v1/performance-mode";
const KEY = ["performanceMode"] as const;

/**
 * Stav režimu. Dotazuje se řídce — odpočet běží v komponentě z `expiresAtEpochMs`,
 * takže sekundový interval by byl zbytečný provoz. Kratší interval při běžícím režimu
 * je tu proto, aby konec (a s ním návrat lišty do klidu) nepřišel se zpožděním.
 */
export function usePerformanceMode() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<PerformanceModeState>(BASE),
    staleTime: 0,
    refetchInterval: (query) => (query.state.data?.active ? 30_000 : 120_000),
  });
}

/**
 * Zapnutí. Odpověď serveru se rovnou zapíše do cache (`setQueryData`), takže odpočet
 * naskočí okamžitě a nečeká se na další dotaz.
 */
export function useEnablePerformanceMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StartPerformanceModeRequest) =>
      apiFetch<PerformanceModeState>(BASE, { method: "POST", json: body }),
    onSuccess: (state) => {
      qc.setQueryData(KEY, state);
      // Zapnutí i vypnutí se hlásí do zvonečku.
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDisablePerformanceMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<PerformanceModeState>(BASE, { method: "DELETE" }),
    onSuccess: (state) => {
      qc.setQueryData(KEY, state);
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/** `mm:ss`, nebo `h:mm:ss` u delších běhů. Nikdy záporné. */
export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
